import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_BILDSCHIRMPRAESENTATION, PRAESENTATIONSART } from "../makros";

export const lektionEinrichten: Lesson = {
  id: "einrichten",
  title: "Bildschirmpräsentation einrichten",
  summary: "Vortrag, Kiosk oder Fenster – und eine Präsentation, zwei Zielgruppen.",
  durationMinutes: 10,
  learningGoals: [
    "Die drei Präsentationsarten unterscheiden und einstellen",
    "Eine zielgruppenorientierte Präsentation aus einer Auswahl von Folien anlegen",
  ],
  steps: [
    {
      id: "praesentationsart",
      title: "Die Präsentationsart",
      explanation: [
        "„Bildschirmpräsentation“ → „Bildschirmpräsentation einrichten“ öffnet den Dialog, " +
          "der festlegt, wie die Präsentation läuft. „Präsentation durch einen Redner“ ist " +
          "der Standard: Vollbild, Weiterschalten per Klick. „Ansicht durch ein Individuum“ " +
          "zeigt sie in einem Fenster – für Präsentationen, die jemand selbst durchklickt.",
        "„Ansicht an einem Kiosk“ ist für Messestände und Empfangsbildschirme gedacht: Die " +
          "Präsentation läuft in einer Endlosschleife, Mausklicks schalten nicht weiter, und " +
          "nur Esc beendet sie. Damit das funktioniert, braucht jede Folie eine " +
          "Anzeigedauer – das Thema der nächsten Lektion.",
        "Im selben Dialog lässt sich festlegen, welche Folien gezeigt werden, ob " +
          "Animationen laufen und auf welchem Monitor.",
      ],
      keyPoints: [
        "Bildschirmpräsentation → Bildschirmpräsentation einrichten.",
        "Redner, Individuum (Fenster), Kiosk (Endlosschleife).",
        "Kiosk braucht Anzeigedauern auf jeder Folie.",
      ],
      task: {
        instruction:
          "Speichere eine Kopie deiner Präsentation und richte sie als Präsentation für einen Messestand ein: „Ansicht an einem Kiosk“.",
        hint: "Bildschirmpräsentation → Bildschirmpräsentation einrichten → „Ansicht an einem Kiosk (volle Bildschirmgröße)“.",
        macro: MAKRO_BILDSCHIRMPRAESENTATION,
        check: () =>
          pruefeMitMakro(MAKRO_BILDSCHIRMPRAESENTATION, (bericht) => {
            const art = bericht.zahl("art") ?? 1;
            return fromCriteria(
              [criterion(`Präsentationsart: ${PRAESENTATIONSART[art] ?? art}`, art === 3)],
              {
                success: "Die Präsentation läuft jetzt in einer Endlosschleife und lässt sich nur mit Esc beenden.",
                failure: "Die Präsentationsart ist noch nicht „Ansicht an einem Kiosk“.",
              },
            );
          }),
      },
    },
    {
      id: "zielgruppen",
      title: "Zielgruppenorientierte Präsentationen",
      explanation: [
        "Oft braucht dieselbe Präsentation mehrere Fassungen: die lange für das Fachpublikum, " +
          "die kurze für die Geschäftsleitung. Statt die Datei zu kopieren, legt " +
          "„Bildschirmpräsentation“ → „Benutzerdefinierte Bildschirmpräsentation“ → " +
          "„Zielgruppenorientierte Präsentationen“ → „Neu“ eine benannte Auswahl von Folien an.",
        "Alle Fassungen nutzen dieselben Folien. Eine Korrektur an einer Folie wirkt in jeder " +
          "Fassung – bei kopierten Dateien müsste man sie überall nachziehen. Gestartet wird " +
          "eine Fassung über dasselbe Menü oder über „Bildschirmpräsentation einrichten“.",
      ],
      keyPoints: [
        "Benutzerdefinierte Bildschirmpräsentation → Neu → Folien auswählen.",
        "Eine Datei, mehrere Fassungen – Korrekturen wirken überall.",
      ],
      task: {
        instruction:
          "Lege eine zielgruppenorientierte Präsentation mit einem Namen an, die nur einen Teil der Folien zeigt.",
        hint: "Bildschirmpräsentation → Benutzerdefinierte Bildschirmpräsentation → Zielgruppenorientierte Präsentationen → Neu.",
        macro: MAKRO_BILDSCHIRMPRAESENTATION,
        check: () =>
          pruefeMitMakro(MAKRO_BILDSCHIRMPRAESENTATION, (bericht) => {
            const folien = bericht.zahl("folien") ?? 0;
            const zielgruppen = [...bericht.gruppe("zielgruppe").values()];
            const teilweise = zielgruppen.filter((z) => {
              const anzahl = Number(z.get("folien") ?? 0);
              return anzahl >= 2 && anzahl < folien;
            });
            return fromCriteria(
              [
                criterion(
                  `Zielgruppenorientierte Präsentationen: ${zielgruppen.map((z) => z.get("name")).join(", ") || "keine"}`,
                  zielgruppen.length > 0,
                ),
                criterion("Eine davon zeigt mindestens zwei, aber nicht alle Folien", teilweise.length > 0),
              ],
              {
                success: "Zwei Fassungen, eine Datei – und jede Korrektur wirkt in beiden.",
                failure: "Es gibt noch keine passende zielgruppenorientierte Präsentation.",
              },
            );
          }),
      },
    },
  ],
};
